import { useLayoutEffect, useRef } from "react";
import { classNames } from "../../helpers/classNames";
import Button from "./Button";

/**
 * Publishes the bar's height as --md-bottom-bar on <html>, so the message bar
 * (util/messageBar.js, via index.css) and anything else pinned to the bottom
 * can sit above it. Cleared when the bar unmounts.
 */
function usePublishedHeight(ref) {
  useLayoutEffect(() => {
    const root = document.documentElement;
    const el = ref.current;
    if (!el) return undefined;
    const update = () => root.style.setProperty("--md-bottom-bar", `${el.offsetHeight}px`);
    update();
    const observer = typeof ResizeObserver !== "undefined" ? new ResizeObserver(update) : null;
    observer?.observe(el);
    window.addEventListener("resize", update);
    return () => {
      observer?.disconnect();
      window.removeEventListener("resize", update);
      root.style.removeProperty("--md-bottom-bar");
    };
  }, [ref]);
}

/**
 * The pinned action bar on mobile and tablet (docs/design/HANDOFF.md,
 * "Bottom action bar"). One full-width primary button; tablet adds a Back
 * button beside it. Anything passed as `children` sits above the buttons (the
 * terms checkbox on step 1); `note` sits under them (a helper line;
 * `noteClassName` styles its row, e.g. md:hidden for a phone-only link).
 *
 * Hidden on desktop, where the side panel carries the same actions. Pages
 * that render it need bottom padding so content can scroll past it:
 * `WizardShell` does that.
 */
const BottomBar = ({ primary, back = null, note = null, noteClassName, children, className }) => {
  const ref = useRef(null);
  usePublishedHeight(ref);
  return (
  <div
    ref={ref}
    className={classNames(
      "fixed inset-x-0 bottom-0 z-40 flex flex-col gap-3 border-t border-md-pink-light-1 bg-white px-4 pt-3.5 lg:hidden",
      "pb-[max(30px,env(safe-area-inset-bottom))] md:px-[90px] md:pt-4 md:pb-[max(28px,env(safe-area-inset-bottom))]",
      className
    )}
  >
    {children}
    <div className="flex gap-3">
      {back ? (
        <Button
          variant="secondary"
          size="lg"
          className="hidden md:inline-flex flex-shrink-0 px-7"
          to={back.to}
          onClick={back.onClick}
          disabled={!!back.disabled}
        >
          {back.label || "Back"}
        </Button>
      ) : null}
      <div className="min-w-0 flex-1">{primary}</div>
    </div>
    {note ? <div className={classNames("text-center text-[13px] text-md-muted", noteClassName)}>{note}</div> : null}
  </div>
  );
};

export default BottomBar;
